/**
 * Stato server della ricerca manuale multi-fonte (F-085).
 *
 * La ricerca **non** parte a ogni battitura: raggiunge OpenFIGI, che ha limiti
 * di frequenza stretti, e il catalogo del broker, che è una richiesta ogni
 * cinquanta secondi. Parte su invio esplicito, e la cache di TanStack Query
 * (60 s di `staleTime`, definiti in `App.tsx`) fa sì che rifare la stessa
 * ricerca non spenda una seconda chiamata.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { AssetDomain } from '@/lib/asset-domain';
import { api } from '@/lib/api-client';

/**
 * Le fonti, per ambito (SPEC-026 §2, F-165): OpenFIGI e il broker per la
 * borsa; CoinGecko e il catalogo dell'exchange dell'utente per le crypto.
 * `catalog` vale per entrambi.
 */
export type SearchSourceName = 'catalog' | 'openfigi' | 'trading212' | 'coingecko' | 'exchange';

/** Il MIC del mercato delle criptovalute: sceglierlo cambia le fonti. */
export const CRYPTO_MIC = 'CRYPTO';

export function searchDomainOf(mic: string): AssetDomain {
  return mic === CRYPTO_MIC ? 'crypto' : 'stock';
}

export const SOURCES_BY_DOMAIN: Record<AssetDomain, SearchSourceName[]> = {
  stock: ['catalog', 'openfigi', 'trading212'],
  crypto: ['catalog', 'coingecko', 'exchange'],
};

/**
 * `ok` = ha risposto, anche con zero righe. `unavailable` = non ha potuto.
 * `skipped` = non è stata interrogata. Senza questa distinzione un elenco
 * vuoto non direbbe se il titolo non esiste o se una fonte era giù.
 */
export type SearchSourceStatus = 'ok' | 'unavailable' | 'skipped';

export type SearchSourceOutcome = {
  source: SearchSourceName;
  status: SearchSourceStatus;
  count: number;
  detail: string | null;
};

export type InstrumentSearchHit = {
  key: string;
  name: string;
  /** Nome pubblico (F-087): qui distingue i simboli che si somigliano. */
  commercialName: string | null;
  isin: string | null;
  /** Simbolo di quotazione, quando lo si conosce con certezza. */
  symbol: string | null;
  /** Ticker come lo scrive la fonte: Bloomberg per OpenFIGI, `AAPL_US_EQ` per il broker. */
  ticker: string | null;
  exchange: string | null;
  mic: string | null;
  marketName: string | null;
  currency: string | null;
  instrumentType: string | null;
  /** L'ambito (SPEC-026 §2.4): dal catalogo è calcolato sulla riga, OpenFIGI e il broker conoscono solo la borsa. */
  domain: AssetDomain;
  figi: string | null;
  trading212Ticker: string | null;
  /** Presente solo se il titolo è già nel nostro catalogo. */
  instrumentId: string | null;
  /** L'utente può aprirne la scheda. Falso anche per un titolo a catalogo che non segue. */
  visible: boolean;
  sources: SearchSourceName[];
  /** Solo crypto (F-165): la coppia sul catalogo dell'exchange dell'utente, es. `BTC-EUR`. */
  exchangePair: string | null;
  /** Solo crypto: posizione per capitalizzazione. `null` = non misurata. */
  marketCapRank: number | null;
  /** Solo crypto: la regola delle top 200 (SPEC-026 §9). `null` = non applicabile. */
  investable: { ok: boolean; reason: string | null } | null;
};

export type InstrumentSearchResponse = {
  query: string;
  /** L'ambito della ricerca (SPEC-026 §2): deciso dal mercato scelto. */
  domain: AssetDomain;
  /**
   * L'ISIN cercato, quando la ricerca **è** un ISIN — `null` altrimenti.
   *
   * Le righe OpenFIGI di questa risposta sono allora le venue di quel titolo,
   * e chi ne mette una a catalogo lo scrive sull'anagrafica: senza, nasce
   * senza ISIN e non si ritrova più cercandola per ISIN.
   */
  queryIsin: string | null;
  items: InstrumentSearchHit[];
  sources: SearchSourceOutcome[];
  /**
   * Righe delle **fonti esterne** scartate dal filtro di mercato. Il catalogo
   * non vi rientra: il suo MIC è persistito e il filtro lo applica il database.
   */
  excludedByMarket: number;
  /** Righe trovate prima del taglio: maggiore di `items.length` = elenco troncato. */
  total: number;
};

export type InstrumentSearchParams = {
  q: string;
  mic: string;
  sources: SearchSourceName[];
};

export const instrumentSearchKeys = {
  all: ['instruments', 'search'] as const,
  query: (params: InstrumentSearchParams) =>
    ['instruments', 'search', params.q, params.mic, params.sources.join(',')] as const,
};

function toQueryString(params: InstrumentSearchParams): string {
  const search = new URLSearchParams({ q: params.q, sources: params.sources.join(',') });
  if (params.mic) search.set('mic', params.mic);
  return search.toString();
}

export type CreateCryptoInstrumentInput = { symbol: string; name?: string };

export type CreatedInstrument = {
  id: string;
  name: string;
  isin: string | null;
  symbol: string | null;
  instrumentType: string;
  currency: string;
};

/**
 * Una crypto entra a catalogo per simbolo (F-165): il server la fa nascere
 * come farebbe la scoperta o la sincronizzazione (SPEC-026 §3.4), non con la
 * forma di un titolo di borsa.
 */
export function useCreateCryptoInstrument() {
  return useMutation({
    mutationFn: (input: CreateCryptoInstrumentInput) => api.post<CreatedInstrument>('/instruments/crypto', input),
  });
}

/** `null` = nessuna ricerca ancora inviata: la query non parte. */
export function useInstrumentSearch(params: InstrumentSearchParams | null) {
  return useQuery({
    queryKey: params ? instrumentSearchKeys.query(params) : instrumentSearchKeys.all,
    queryFn: ({ signal }) =>
      api.get<InstrumentSearchResponse>(`/instruments/search?${toQueryString(params!)}`, signal),
    enabled: params !== null,
  });
}
